import { Link, Text } from "@certn/cascadia";
import {
  BrandAsset,
  mq,
  Grid,
  GridItem,
  Divider,
  useColors,
  Container,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Trans, useTranslation } from "react-i18next";

import { AnonymizedUsagesTable } from "./AnonymizedUsagesTable";
import { CustomerAddress } from "./CustomerAddress";
import { Header } from "./Header";
import { PaidInfo } from "./PaidInfo";
import { PaymentFooter } from "./PaymentFooter";
import { Segments } from "./Segments";
import { getCertnAddress } from "./utils";
import { InvoiceProps } from "../../../../src/schemas-and-types";

export const Invoice = (props: InvoiceProps) => {
  const colors = useColors();
  const { t } = useTranslation();
  const { invoiceData } = props;
  const subsidiary = invoiceData.subsidiary;
  const isClient = invoiceData.billing_profile.payer_option === "CLIENT";
  const {
    administrative_division_1: province,
    country,
    locality: city,
    postal_code: postalCode,
    address_line_1: addressLine1,
    unit_number: unitNumber,
  } = invoiceData.billing_profile.address || {};

  const { enableInvoicePayments } = useFlags();
  const certnAddress = getCertnAddress(
    invoiceData.subsidiary,
    invoiceData.issue_date
  );

  const testBanner = !invoiceData.is_production ? (
    <GridItem span={12} alignItems="center" className={styles.testBanner}>
      {t("TEST INVOICE - DO NOT PAY")}
    </GridItem>
  ) : null;

  // Intercheck is currently the only subsidairy with a different brand logo
  const brand = subsidiary === "INTERCHECK" ? "intercheck" : "certn";

  return (
    <Container layout="wide" className={styles.container}>
      <Grid layout="full-width">
        {testBanner}
        <GridItem span={8} alignItems="center" className={styles.logoAndTitle}>
          <BrandAsset brand={brand} type="full" height={48} />
          <Divider isVertical borderLeftColor={colors["brand-certn-moss"]} />
          <Text style="global/title-large" color="brand-certn-moss" align="end">
            {subsidiary === "INTERCHECK" ? t("Tax Invoice") : t("Invoice")}
          </Text>
        </GridItem>

        <GridItem span={4} alignItems="end">
          {certnAddress}
        </GridItem>

        <GridItem span={6}>
          <CustomerAddress
            componentName="address"
            name={invoiceData.billing_profile.customer_name || "-"}
            street={`${unitNumber ? `${unitNumber}-` : ""}${addressLine1}`}
            city={city || "-"}
            province={province || "-"}
            postalCode={postalCode || "-"}
            country={country || "-"}
            isClient={isClient}
          />
        </GridItem>

        <GridItem span={6}>
          <Header invoiceData={invoiceData} isClient={isClient} />
          {testBanner}
        </GridItem>

        <Divider
          borderLeftColor={colors["brand-certn-moss"]}
          className={styles.primaryDivider}
        />

        <Segments invoiceData={invoiceData} isClient={isClient} />

        <Divider
          borderLeftColor={colors["brand-certn-moss"]}
          className={styles.primaryDivider}
        />

        {invoiceData.status !== "PAID" && (
          <PaymentFooter invoiceData={invoiceData} />
        )}

        {invoiceData.status === "PAID" && (
          <PaidInfo invoiceData={invoiceData} />
        )}

        {isClient && (
          <div className={styles.usagesWrapper}>
            <Text style="global/title-large">{t("Line Item Details")}</Text>
            <br />
            {enableInvoicePayments && invoiceData.client_portal_invoice_link ? (
              <Text style="global/body-primary">
                <Trans>
                  To protect the privacy of your applicants, we have not
                  included any directly identifying information in this
                  unencrypted document. For a version of the report which
                  includes applicant details, please login to{" "}
                  <Link
                    href={invoiceData.client_portal_invoice_link}
                    testID="client-portal-link"
                  >
                    Certn Client Portal
                  </Link>{" "}
                  .
                </Trans>
              </Text>
            ) : (
              <Text style="global/body-primary">
                <Trans>
                  To protect the privacy of your applicants, we have not
                  included any directly identifying information in this
                  unencrypted document. For a version of the report which
                  includes applicant details, please contact our billing team
                  (billing@certn.co).
                </Trans>
              </Text>
            )}
            <br />
            <br />
            <AnonymizedUsagesTable invoiceData={invoiceData} />
          </div>
        )}
      </Grid>
    </Container>
  );
};

const styles = {
  container: css(
    {
      margin: "auto",
    },
    // A4 paper size as per design. (NOT letter size)
    mq({
      width: ["21cm", "21cm", "21cm", "100%"],
      minHeight: "29.7cm",
    })
  ),
  logoAndTitle: css({
    // Need to increase specificity to override the GridItem styles
    "&": {
      // display: "flex",
      flexDirection: "row",
    },
    "& svg": {
      // The SVG component iteslef seems to come with a space-6 margin but we want it left-aligned to the edge of the page
      marginLeft: "calc(-1 * var(--space-6))",
    },
    "& hr": {
      height: "100%",
      marginRight: "var(--space-6)",
    },
  }),
  primaryDivider: css({
    // Need to increase specificity to override the non-grid item styles
    "&": {
      borderTopWidth: 2,
    },
  }),

  // Usages
  usagesWrapper: css({
    breakBefore: "page",
    // marginTop: "var(--space-6)",
  }),

  testBanner: css({
    color: "#AB0000",
    fontWeight: 800,
    textAlign: "center",
    fontSize: "1em",
    marginBottom: "calc(var(--space-7)*-1)",
  }),
};
